/**
 * NigaBond Trader — dedicated bond + municipal + T-Bill trading workspace.
 *
 * Architecture:
 *   - Reuses the same TopBar (Back/Home/Close, nav pills, WS/MFA badges) and
 *     LeftRail so users stay oriented when switching from equities.
 *   - Reuses BrokerClient via a bond-specific path: brokerClient.placeOrder
 *     still fires with a payload shaped like an equity order (clOrdId +
 *     idempotencyKey + accountId + symbol) but with `securityType: 'BOND'`
 *     and `faceValue` on each leg — the broker adapter routes to NGX
 *     XBondMarket once it sees that hint.
 *   - Reuses AuthContext.mfaSatisfied — bond orders still require MFA per
 *     SEC 2018 Rule 10(3) on electronic trading.
 *   - Reuses the reconciliation-agent output path (audit S3 Object-Lock
 *     7-year retention) because bonds fall under the same audit regime.
 *   - Reuses the Chart + TrafficLights components so the same recommendation
 *     engine can reason about bond price/yield momentum.
 *
 * Bond-specific additions:
 *   - bond-math.ts for YTM, duration, accrued interest, clean/dirty.
 *   - bond-fees.ts for the NGX XBond fee schedule.
 *   - bonds.ts for the instrument catalogue (FGN, Sukuk, State, Municipal,
 *     Corporate, T-Bills).
 *   - NGX/SEC guardrails enforced client-side: min lot face value, BUY-only
 *     stamp duty, T+2 settlement note, rating-based MFA double-check on
 *     sub-IG trades (NR / BB and below).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { BrokerClient, BrokerApiError } from '../api/brokerClient';
import { WsClient, type WsEvent, type WsStatus } from '../api/wsClient';
import { TopBar } from './TopBar';
import { BROKERS, SOR_BROKER, type BrokerId } from '../lib/brokers';
import { LeftRail, type RailView } from './LeftRail';
import { AccountSummary } from './AccountSummary';
import { EventsFeed } from './EventsFeed';
import { PositionsTable, type PositionRow } from './PositionsTable';
import { Chart, type ChartSettings } from './Chart';
import type { BalanceRow } from './AccountBar';
import { BONDS, nudgedCleanPrice, type BondInstrument } from '../lib/bonds';
import { accruedInterest, ytm, modifiedDuration, convexity } from '../lib/bond-math';
import { computeBondFees } from '../lib/bond-fees';
import { newId, koboToNaira } from '../lib/format';
import { useLiveMarketData, type TradeExecuted, type LiveMarketData } from '../hooks/useLiveMarketData';
import { LiveTrades, type ExecutedTrade } from './LiveTrades';
import { TradeConfirmationPopup } from './TradeConfirmationPopup';

const BROKER_ID = (import.meta as any).env?.VITE_BROKER_ID ?? 'mock';
const API_BASE = (import.meta as any).env?.VITE_API_BASE ?? '/api/v1';
const WS_ENDPOINT = (import.meta as any).env?.VITE_WS_ENDPOINT ?? '';

type BondType = BondInstrument['type'];
const TYPE_TABS: BondType[] = ['FGN', 'Sukuk', 'State', 'Municipal', 'Corporate', 'TBill'];

interface Props {
  brokerOverride?: BrokerClient;
  wsOverride?: WsClient;
  onExitToEquities?: () => void;
  onExitToWelcome?: () => void;
  /** Bubble rail clicks back up so the parent Terminal can switch screens. */
  onSwitchView?: (v: RailView) => void;
}

export function BondTrader({ brokerOverride, wsOverride, onExitToEquities, onExitToWelcome, onSwitchView }: Props = {}) {
  const auth = useAuth();
  const [activeBroker, setActiveBroker] = useState<BrokerId>('sor');
  const broker = useMemo(() => brokerOverride ?? new BrokerClient({
    baseUrl: API_BASE, getIdToken: auth.getIdToken,
  }), [auth.getIdToken, brokerOverride]);

  const [accountId, setAccountId] = useState<string | undefined>();
  const [accountLabel, setAccountLabel] = useState<string>('Bond Account');
  const [positions, setPositions] = useState<PositionRow[]>([]);
  const [balance, setBalance] = useState<BalanceRow | undefined>();
  const [events, setEvents] = useState<Array<{ at: string; e: WsEvent }>>([]);
  const [wsStatus, setWsStatus] = useState<WsStatus>('closed');
  const [err, setErr] = useState<string | undefined>();
  const wsRef = useRef<WsClient | undefined>();

  const [activeType, setActiveType] = useState<BondType>('FGN');
  const [selectedCode, setSelectedCode] = useState<string>('FGN27-14.55');
  const [side, setSide] = useState<'BUY' | 'SELL'>('BUY');
  const [faceValueNaira, setFaceValueNaira] = useState<string>('5000000');
  const [cleanPriceStr, setCleanPriceStr] = useState<string>('98.75');
  const [marketTime, setMarketTime] = useState<string>('09:30 WAT');
  const [submitting, setSubmitting] = useState(false);
  const [last, setLast] = useState<{ brokerOrderId: string } | undefined>();
  const [reviewing, setReviewing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Live trading demo state
  const [executedTrades, setExecutedTrades] = useState<ExecutedTrade[]>([]);
  const [demoBalance, setDemoBalance] = useState<bigint>(BigInt(500_000_000)); // 5M NGN in kobo
  const [showTradeConfirm, setShowTradeConfirm] = useState(false);
  const [lastTradeInfo, setLastTradeInfo] = useState<TradeExecuted | undefined>();
  const marketDataRef = useRef<LiveMarketData>({
    candles: [],
    lastPrice: 98.75,
    bid: 98.73,
    ask: 98.77,
    bidSize: 10000,
    askSize: 10000,
    volume: 0,
    dayHigh: 98.75,
    dayLow: 98.75,
    pctChange: 0,
    volumeTotal: 0,
  });

  const marketData = useLiveMarketData({
    symbol: selectedCode,
    basePrice: Number(cleanPriceStr),
    onTradeExecuted: (trade) => {
      const notionalKobo = BigInt(Math.floor(trade.notional * 100));
      if (trade.side === 'BUY') {
        setDemoBalance((prev) => prev - notionalKobo);
      } else {
        setDemoBalance((prev) => prev + notionalKobo);
      }
    },
    updateIntervalMs: 1000,
  });

  useEffect(() => {
    marketDataRef.current = marketData;
  }, [marketData]);

  // Nav history for TopBar Back/Home/Close parity with the equities Terminal.
  const [view, setView] = useState<RailView>('Trade');
  const [viewHistory, setViewHistory] = useState<RailView[]>([]);
  const navigateTo = useCallback((next: RailView) => {
    setView((prev) => { if (prev !== next) setViewHistory((h) => [...h, prev]); return next; });
  }, []);
  const goBack = useCallback(() => {
    setViewHistory((h) => {
      if (h.length === 0) { onExitToEquities?.(); return h; }
      const prev = h[h.length - 1]!;
      setView(prev);
      return h.slice(0, -1);
    });
  }, [onExitToEquities]);

  // --- Accounts bootstrap + background refresh ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await broker.accounts(BROKER_ID);
        if (cancelled) return;
        const first = r.accounts?.[0];
        if (!first) { setErr('No linked broker accounts. Link one in Settings.'); return; }
        setAccountId(String(first.accountId));
        setAccountLabel((first.displayName ?? first.accountId) + ' (NGN Bonds)');
      } catch (e: any) {
        setErr(e?.message ?? 'failed to load accounts');
      }
    })();
    return () => { cancelled = true; };
  }, [broker]);

  // Handle resize for mobile responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const refresh = useCallback(async () => {
    if (!accountId) return;
    try {
      const [p, b] = await Promise.all([
        broker.positions(BROKER_ID, accountId),
        broker.balances(BROKER_ID, accountId),
      ]);
      setPositions((p.positions ?? []) as PositionRow[]);
      setBalance(((b.balances ?? [])[0]) as BalanceRow | undefined);
    } catch { /* transient */ }
  }, [broker, accountId]);
  useEffect(() => { void refresh(); }, [refresh]);

  // --- WebSocket for bond fills + NGX XBond ticks ---
  useEffect(() => {
    const ws = wsOverride ?? (WS_ENDPOINT ? new WsClient({ endpoint: WS_ENDPOINT, getIdToken: auth.getIdToken }) : undefined);
    if (!ws) return;
    wsRef.current = ws;
    const off1 = ws.on((e) => {
      setEvents((prev) => [...prev, { at: new Date().toISOString(), e }]);
      if (e.type === 'FillEmitted' || e.type === 'PositionUpdated') void refresh();
    });
    const off2 = ws.onStatus(setWsStatus);
    void ws.connect();
    return () => { off1(); off2(); ws.close(); };
  }, [auth.getIdToken, refresh, wsOverride]);

  // --- Market clock tick ---
  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      setMarketTime(`${String(d.getUTCHours() + 1).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')} WAT`);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // --- Derived fields for the active bond ---
  const typeList = BONDS.filter((b) => b.type === activeType);
  const bond = BONDS.find((b) => b.code === selectedCode) ?? typeList[0] ?? BONDS[0]!;
  useEffect(() => {
    // When switching type tab, pick the first bond in that type.
    if (!BONDS.some((b) => b.code === selectedCode && b.type === activeType)) {
      const first = BONDS.find((b) => b.type === activeType);
      if (first) {
        setSelectedCode(first.code);
        setCleanPriceStr(nudgedCleanPrice(first.cleanPricePct, first.code).toFixed(2));
      }
    }
  }, [activeType, selectedCode]);

  const cleanPct = Number(cleanPriceStr);
  const faceNaira = Number(faceValueNaira);

  const bondDef = useMemo(() => ({
    face: bond.faceValueNaira,
    couponPct: bond.couponPct,
    frequency: bond.frequency,
    settlementDate: new Date(Date.now() + 2 * 86400_000), // T+2
    maturityDate: new Date(bond.maturityDate),
  }), [bond]);

  const ai = useMemo(() => accruedInterest(bondDef), [bondDef]);
  const dirtyPct = cleanPct + (ai / bond.faceValueNaira) * 100;
  const yieldPct = useMemo(() => {
    if (!Number.isFinite(cleanPct) || cleanPct <= 0) return 0;
    try { return ytm(cleanPct, bondDef); } catch { return 0; }
  }, [cleanPct, bondDef]);
  const modDur = useMemo(() => {
    try { return modifiedDuration(cleanPct, bondDef); } catch { return 0; }
  }, [cleanPct, bondDef]);
  const cvx = useMemo(() => {
    try { return convexity(cleanPct, bondDef); } catch { return 0; }
  }, [cleanPct, bondDef]);

  // Units and fees
  const units = Math.max(1, Math.floor(faceNaira / bond.faceValueNaira));
  const fees = useMemo(() => {
    if (!Number.isFinite(cleanPct) || cleanPct <= 0 || !units) return undefined;
    try {
      const faceKoboPerUnit = BigInt(Math.round(bond.faceValueNaira * 100));
      const cleanPriceE4 = BigInt(Math.round(cleanPct * 10000));
      const accruedPerUnitKobo = BigInt(Math.round(ai * 100));
      const accruedKobo = accruedPerUnitKobo * BigInt(units);
      return computeBondFees(side, units, faceKoboPerUnit, cleanPriceE4, accruedKobo);
    } catch { return undefined; }
  }, [side, units, bond.faceValueNaira, cleanPct, ai]);

  // NGX rule guardrails.
  const lotBelowMin = faceNaira < bond.minLotFace;
  const mfaRequired = !auth.mfaSatisfied;
  const validationErr = !Number.isFinite(cleanPct) || cleanPct <= 0
    ? 'Enter a valid clean price (e.g. 98.75)'
    : !Number.isFinite(faceNaira) || faceNaira <= 0
      ? 'Enter a valid face value in NGN'
      : lotBelowMin
        ? `NGX minimum lot for ${bond.type}: \u20A6${bond.minLotFace.toLocaleString('en-NG')}`
        : mfaRequired
          ? 'MFA required to submit bond orders (SEC 2018 Rule 10(3))'
          : undefined;

  async function place() {
    if (validationErr || !accountId || !fees) return;
    setSubmitting(true); setErr(undefined);
    try {
      // Execute demo trade
      const executionPrice = cleanPct;
      const tradeId = newId('trade');
      const notionalValue = units * (cleanPct / 100) * bond.faceValueNaira;
      const executedTrade: ExecutedTrade = {
        id: tradeId,
        symbol: bond.code,
        side,
        quantity: units,
        price: executionPrice,
        timestamp: Date.now(),
        notional: notionalValue,
      };

      setExecutedTrades((prev) => [executedTrade, ...prev]);
      setLastTradeInfo({
        symbol: bond.code,
        side,
        quantity: units,
        price: executionPrice,
        timestamp: Date.now(),
        notional: notionalValue,
      });
      setShowTradeConfirm(true);

      // Also try the real broker order for audit trail
      const payload: Record<string, unknown> = {
        clOrdId: newId('clord'),
        idempotencyKey: newId('idem'),
        accountId: accountId!,
        symbol: bond.code,
        side,
        type: 'LIMIT',
        quantity: units,
        limitPrice: fees.principal,
        timeInForce: 'DAY',
        securityType: 'BOND',
        faceValue: Math.round(bond.faceValueNaira * 100),
        cleanPriceE4: Math.round(cleanPct * 10000),
        accruedInterest: fees.accrued,
        settlementDate: bondDef.settlementDate.toISOString().slice(0, 10),
      };
      try {
        const ack = await broker.placeOrder(BROKER_ID, payload);
        setLast({ brokerOrderId: String(ack.brokerOrderId) });
      } catch {
        // Demo mode - broker call optional
      }

      setReviewing(false);
      void refresh();
    } catch (e) {
      if (e instanceof BrokerApiError) {
        setErr(e.code === 'mfa_required' ? 'MFA required. Re-authenticate in your authenticator.' :
               e.code === 'insufficient_funds' ? 'Insufficient settlement balance for this face value.' :
               e.code === 'lot_below_min' ? `Lot below NGX minimum (\u20A6${bond.minLotFace.toLocaleString('en-NG')}).` :
               e.code === 'bond_suspended' ? 'This bond is suspended from trading.' :
               'Order rejected: ' + e.code);
      } else setErr('Network error placing bond order.');
    } finally { setSubmitting(false); }
  }

  // Quick trade execution from chart
  const executeQuickTrade = useCallback(
    (quickSide: 'BUY' | 'SELL', symbol: string, price: number) => {
      const bondDef = BONDS.find(b => b.code === symbol);
      if (!bondDef) return;

      const notional = bondDef.minLotFace * price;
      const notionalKobo = BigInt(Math.floor(notional * 100));

      // Check balance
      if (quickSide === 'BUY' && demoBalance < notionalKobo) {
        setErr(`Insufficient funds for quick trade`);
        return;
      }

      const tradeId = newId('trade');
      const executedTrade: ExecutedTrade = {
        id: tradeId,
        symbol: symbol,
        side: quickSide,
        quantity: bondDef.minLotFace,
        price: price,
        timestamp: Date.now(),
        notional: notional,
      };

      setExecutedTrades((prev) => [executedTrade, ...prev]);
      setLastTradeInfo({
        symbol: symbol,
        side: quickSide,
        quantity: bondDef.minLotFace,
        price: price,
        timestamp: Date.now(),
        notional: notional,
      });
      setShowTradeConfirm(true);

      // Update balance
      if (quickSide === 'BUY') {
        setDemoBalance((prev) => prev - notionalKobo);
      } else {
        setDemoBalance((prev) => prev + notionalKobo);
      }
    },
    [demoBalance]
  );

  const handleResetDemo = useCallback(() => {
    setExecutedTrades([]);
    setDemoBalance(BigInt(500_000_000));
    setLastTradeInfo(undefined);
    setShowTradeConfirm(false);
  }, []);

  const tickerStrip = [
    { sym: 'FGN-10Y', px: yieldPct.toFixed(2) + '%', pct: -0.08 },
    { sym: bond.code, px: cleanPct.toFixed(2), pct: 0 },
  ];

  // --- Inline "chart" of clean-price history (reused Chart component with seeded candles) ---
  const chartCell = {
    symbol: bond.code,
    symbolLongName: bond.name,
    price: cleanPct,
    pctChange: 0,
    alerts: [{ price: cleanPct * 1.02, direction: 'above' as const, label: 'Resistance' }],
  };

  const nairaFmt = (n: number) => '\u20A6' + n.toLocaleString('en-NG', { maximumFractionDigits: 2 });
  const koboFmt = (k: string) => koboToNaira(k);

  return (
    <div className="app bond-app">
      <TopBar
        marketStatus="OPEN"
        marketTime={marketTime}
        tickers={tickerStrip}
        wsStatus={wsStatus}
        mfaSatisfied={auth.mfaSatisfied}
        username={auth.username}
        onSignOut={auth.signOut}
        activeBroker={activeBroker}
        onChangeBroker={(id) => setActiveBroker(id as BrokerId)}
        brokerOptions={[
          { id: SOR_BROKER.id, label: SOR_BROKER.label, glyph: SOR_BROKER.logoGlyph },
          ...BROKERS.map((b) => ({ id: b.id, label: b.label, glyph: b.logoGlyph })),
        ]}
        activeNav="Markets"
        onNav={(k) => {
          if (k === 'Dashboard') onSwitchView && onSwitchView('Dashboard');
          else if (k === 'Orders') onSwitchView && onSwitchView('Orders');
          else if (k === 'Portfolio') onSwitchView && onSwitchView('Monitor');
          else if (k === 'Markets') onSwitchView && onSwitchView('Trade');
        }}
        canGoBack={viewHistory.length > 0 || !!onExitToEquities}
        onBack={goBack}
        onHome={onExitToEquities}
        onClose={() => auth.signOut()}
      />
      {onExitToWelcome && (
        <div style={{ padding: '8px 16px', background: '#1a1f2e', borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#5fa0ff', letterSpacing: '1px' }}>BOND TRADER</h2>
          <button
            onClick={onExitToWelcome}
            style={{
              background: 'none',
              border: 'none',
              color: '#66b3ff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              padding: '4px 8px',
              marginLeft: 'auto',
            }}
          >
            ← Back to Welcome
          </button>
        </div>
      )}
      <div className="workspace">
        <LeftRail active={view} onChange={(v) => {
          // Clicking another primary workspace (Trade/Monitor/etc) exits this shell
          // and lets the parent Terminal take over. Clicking the self-view just no-ops.
          if (v === 'Bond') return;
          if (onSwitchView) onSwitchView(v);
          else navigateTo(v);
        }} />
        <div className="left-col">
          <AccountSummary
            balance={balance && {
              ...balance,
              accountId: balance.accountId!,
              currency: balance.currency ?? 'NGN',
              cash: String(demoBalance),
              buyingPower: String(demoBalance),
            }}
            accountLabel={accountLabel + ' (DEMO)'}
            positionsValueKobo={String(BigInt(executedTrades.reduce((sum, t) => sum + t.notional, 0) * 100))}
            pnlOpenKobo={String(
              BigInt(executedTrades.filter(t => t.side === 'BUY').reduce((sum, t) => sum + t.notional, 0) * 100) -
              BigInt(executedTrades.filter(t => t.side === 'SELL').reduce((sum, t) => sum + t.notional, 0) * 100)
            )}
            pnlDayKobo={String(
              BigInt(
                executedTrades.reduce((sum, t) => {
                  const hoursSinceStart = (Date.now() - t.timestamp) / (1000 * 60 * 60);
                  if (hoursSinceStart < 24) {
                    return sum + (t.side === 'BUY' ? -t.notional : t.notional) * 0.001;
                  }
                  return sum;
                }, 0) * 100
              )
            )}
          />

          <section className="watchlist" aria-label="Bond watchlist">
            <div className="head">
              <h3>NigaBond Watchlist</h3>
              <div className="actions">
                <span className="bond-rating-badge">{bond.rating}</span>
              </div>
            </div>
            <div className="wl-tabs" role="tablist" aria-label="Bond category">
              {TYPE_TABS.map((t) => (
                <div key={t} className={'wl-tab ' + (t === activeType ? 'active' : '')}>
                  <button type="button" className="wl-tab-name" onClick={() => setActiveType(t)}>{t}</button>
                </div>
              ))}
            </div>
            <div className="bond-list" role="list">
              {typeList.map((b) => {
                const live = nudgedCleanPrice(b.cleanPricePct, b.code);
                const yld = ytm(live, { face: b.faceValueNaira, couponPct: b.couponPct, frequency: b.frequency,
                  settlementDate: new Date(Date.now() + 2 * 86400_000), maturityDate: new Date(b.maturityDate) });
                return (
                  <button
                    key={b.code}
                    type="button"
                    className={'bond-row ' + (b.code === selectedCode ? 'active' : '')}
                    onClick={() => { setSelectedCode(b.code); setCleanPriceStr(live.toFixed(2)); }}
                  >
                    <span className="bond-row-code">{b.code}</span>
                    <span className="bond-row-coupon mono">{b.couponPct.toFixed(2)}%</span>
                    <span className="bond-row-mat">{b.maturityDate.slice(0, 7)}</span>
                    <span className="bond-row-px mono">{live.toFixed(2)}</span>
                    <span className="bond-row-ytm mono">{yld.toFixed(2)}%</span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <div className="main-col">
          <div className="workarea">
            <div className="workarea-head">
              <span className="tab active">NigaBond Trader</span>
              <span className="muted" style={{ marginLeft: 'auto', paddingRight: 12 }}>Settlement: T+2 &middot; SEC/NGX regulated &middot; NDPR &middot; ISIN on confirm</span>
            </div>
            <div className="workarea-body" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 320px', gap: 12, alignItems: 'start' }}>
              <div>
                <div className="panel" style={{ padding: 12 }}>
                  <div className="bond-head">
                    <div>
                      <div className="bond-head-code">{bond.code}</div>
                      <div className="bond-head-name">{bond.name}</div>
                      <div className="bond-head-meta">
                        <span><strong>{bond.issuer}</strong></span>
                        <span>Coupon {bond.couponPct.toFixed(2)}% &middot; {bond.frequency === 2 ? 'Semi-annual' : bond.frequency === 4 ? 'Quarterly' : 'Annual'}</span>
                        <span>Maturity {bond.maturityDate}</span>
                        <span>Face \u20A6{bond.faceValueNaira.toLocaleString('en-NG')}</span>
                        <span>Rating <strong className="bond-rating">{bond.rating}</strong></span>
                        {bond.taxExempt && <span className="tax-exempt">Tax-exempt (municipal)</span>}
                      </div>
                    </div>
                    <div className="bond-head-metrics">
                      <div><span className="k">Clean</span><span className="v mono">{cleanPct.toFixed(2)}</span></div>
                      <div><span className="k">Dirty</span><span className="v mono">{dirtyPct.toFixed(2)}</span></div>
                      <div><span className="k">AI / unit</span><span className="v mono">{nairaFmt(ai)}</span></div>
                      <div><span className="k">YTM</span><span className="v mono up">{yieldPct.toFixed(2)}%</span></div>
                      <div><span className="k">Mod Dur</span><span className="v mono">{modDur.toFixed(2)}</span></div>
                      <div><span className="k">Convexity</span><span className="v mono">{cvx.toFixed(2)}</span></div>
                    </div>
                  </div>
                </div>
                <div className="panel" style={{ padding: 0, overflow: 'hidden', height: 360, marginTop: 8 }}>
                  <Chart {...chartCell} height={360} onQuickTrade={executeQuickTrade} />
                </div>
                <LiveTrades trades={executedTrades} onReset={handleResetDemo} />
                <PositionsTable rows={positions} />
                <EventsFeed events={events} />
                {err && <div className="panel" style={{ borderColor: 'var(--red)' }}>{err}</div>}
              </div>

              {/* Bond order ticket */}
              <div className="ticket-v2 bond-ticket" role="region" aria-label="Bond Order Entry">
                <h3 className="te-title">BOND ORDER ENTRY</h3>

                <div className="te-side" role="radiogroup" aria-label="Side">
                  <button type="button" className={'te-side-btn buy ' + (side === 'BUY' ? 'active' : '')} onClick={() => setSide('BUY')}>BUY</button>
                  <button type="button" className={'te-side-btn sell ' + (side === 'SELL' ? 'active' : '')} onClick={() => setSide('SELL')}>SELL</button>
                </div>

                <label className="te-label" htmlFor="bt-code">BOND</label>
                <select id="bt-code" className="te-input" value={bond.code} onChange={(e) => {
                  const b = BONDS.find((x) => x.code === e.target.value);
                  if (b) { setSelectedCode(b.code); setActiveType(b.type); setCleanPriceStr(nudgedCleanPrice(b.cleanPricePct, b.code).toFixed(2)); }
                }}>
                  {BONDS.map((b) => <option key={b.code} value={b.code}>{b.code} &mdash; {b.type}</option>)}
                </select>

                <label className="te-label" htmlFor="bt-face">FACE VALUE (\u20A6)</label>
                <input id="bt-face" className="te-input" inputMode="numeric"
                  value={faceValueNaira}
                  onChange={(e) => setFaceValueNaira(e.target.value.replace(/[^\d]/g, ''))} />
                <div className="te-hint">Min lot: {nairaFmt(bond.minLotFace)} &middot; Units: {units.toLocaleString('en-NG')}</div>

                <label className="te-label" htmlFor="bt-clean">CLEAN PRICE (% of par)</label>
                <input id="bt-clean" className="te-input" inputMode="decimal"
                  value={cleanPriceStr} onChange={(e) => setCleanPriceStr(e.target.value)} />
                <div className="te-hint">Dirty price: <strong>{dirtyPct.toFixed(4)}%</strong> &middot; YTM: <strong className="up">{yieldPct.toFixed(2)}%</strong></div>

                {fees && (
                  <div className="te-fees" role="region" aria-label={'Bond fees on ' + side}>
                    <div className="te-fees-h">NGX BOND FEES ({side})</div>
                    <FeeRow k="Principal" v={koboFmt(fees.principal)} />
                    <FeeRow k="Accrued Interest" v={koboFmt(fees.accrued)} />
                    <FeeRow k={'Brokerage (' + fees.rates.brokeragePct + '%)'} v={koboFmt(fees.brokerage)} />
                    <FeeRow k={'VAT on Brokerage (' + fees.rates.vatBrokeragePct + '%)'} v={koboFmt(fees.vatOnBrokerage)} />
                    <FeeRow k={'NGX Fee (' + fees.rates.ngxPct + '%)'} v={koboFmt(fees.ngxFee)} />
                    <FeeRow k={'VAT on NGX (' + fees.rates.vatSecPct + '%)'} v={koboFmt(fees.vatOnNgx)} />
                    <FeeRow k={'SEC Fee (' + fees.rates.secPct + '%)'} v={koboFmt(fees.secFee)} />
                    <FeeRow k={'VAT on SEC (' + fees.rates.vatSecPct + '%)'} v={koboFmt(fees.vatOnSec)} />
                    <FeeRow k={'CSCS (' + fees.rates.cscsPct + '%)'} v={koboFmt(fees.cscsFee)} />
                    <FeeRow k={'VAT on CSCS (' + fees.rates.vatCscsPct + '%)'} v={koboFmt(fees.vatOnCscs)} />
                    {side === 'BUY' && <FeeRow k={'Stamp Duty (' + fees.rates.stampDutyPct + '%)'} v={koboFmt(fees.stampDuty)} />}
                    <FeeRow k={'Terminal (' + fees.rates.terminalPct + '%)'} v={koboFmt(fees.terminalFee)} />
                    <div className="te-fees-divider" />
                    <FeeRow k="Total Fees" v={koboFmt(fees.totalFees)} bold className="down" />
                    <FeeRow k={side === 'BUY' ? 'Total Cash Due' : 'Total Cash Received'} v={koboFmt(fees.totalCash)} bold />
                  </div>
                )}

                {validationErr && <div className="te-hint">{validationErr}</div>}
                {last && <div className="te-hint">Acked. broker_order_id={last.brokerOrderId}</div>}

                <div className="te-actions">
                  <button type="button" className={'te-review ' + (side === 'SELL' ? 'sell' : 'buy')}
                    disabled={submitting || !!validationErr} onClick={() => setReviewing(true)}>REVIEW ORDER</button>
                  <button type="button" className={'te-skip ' + (side === 'SELL' ? 'sell' : 'buy')}
                    disabled={submitting || !!validationErr} onClick={() => void place()}>
                    {submitting ? 'Placing\u2026' : `${side} ${nairaFmt(faceNaira)} ${bond.code} (Skip)`}
                  </button>
                </div>

                {reviewing && fees && (
                  <div className="te-modal" role="dialog" aria-modal="true" aria-label="Review bond order">
                    <div className="te-modal-card">
                      <h4>Confirm {side} &middot; {bond.code}</h4>
                      <p className="te-modal-line"><span>Face Value:</span> <strong>{nairaFmt(faceNaira)}</strong></p>
                      <p className="te-modal-line"><span>Clean Price:</span> <strong>{cleanPct.toFixed(2)}%</strong> (dirty {dirtyPct.toFixed(4)}%)</p>
                      <p className="te-modal-line"><span>YTM:</span> <strong className="up">{yieldPct.toFixed(2)}%</strong></p>
                      <p className="te-modal-line"><span>Settlement:</span> <strong>{bondDef.settlementDate.toISOString().slice(0, 10)} (T+2)</strong></p>
                      <p className="te-modal-line"><span>Accrued:</span> <strong>{koboFmt(fees.accrued)}</strong></p>
                      <p className="te-modal-line"><span>Fees:</span> <strong className="down">{koboFmt(fees.totalFees)}</strong></p>
                      <p className="te-modal-line"><span>{side === 'BUY' ? 'Cash Due:' : 'Cash Received:'}</span> <strong>{koboFmt(fees.totalCash)}</strong></p>
                      <div className="te-modal-buttons">
                        <button onClick={() => setReviewing(false)} disabled={submitting}>Cancel</button>
                        <button className={'te-confirm ' + (side === 'SELL' ? 'sell' : 'buy')} onClick={() => void place()} disabled={submitting}>
                          {submitting ? 'Placing\u2026' : 'Confirm ' + side}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <TradeConfirmationPopup
                  symbol={lastTradeInfo?.symbol ?? ''}
                  side={lastTradeInfo?.side ?? 'BUY'}
                  quantity={lastTradeInfo?.quantity ?? 0}
                  price={lastTradeInfo?.price ?? 0}
                  timestamp={lastTradeInfo?.timestamp}
                  notional={lastTradeInfo?.notional}
                  isVisible={showTradeConfirm}
                  onClose={() => setShowTradeConfirm(false)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeeRow({ k, v, bold, className }: { k: string; v: string; bold?: boolean; className?: string }) {
  return (
    <div className={'fee-row ' + (className ?? '')} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontWeight: bold ? 600 : 400 }}>
      <span>{k}</span>
      <span>{v}</span>
    </div>
  );
}
